import { TriangleAlert } from "lucide-react";
import { useEffect, useRef, useState, type ReactNode } from "react";
import type { NumberFormat } from "../../shared/normalize";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { createUpload, errorText, getProfiles, saveProfile } from "@/lib/client";
import { sourceTypeText } from "../../shared/sources";
import { versionLabel } from "../../shared/upload";
import { formatDate, formatNumber } from "../../shared/versions";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ErrorLines } from "@/components/ErrorAlert";
import { NativeSelect } from "@/components/ui/native-select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { DropZone } from "@/components/DropZone";
import type { PreviewResponse, UploadResponse } from "../../shared/api";
import {
  SOURCE_TYPE_CODES, canUpload, needsJustification, counterText, draftFrom, emptyDraft, fieldLabel, FORMAT_LABEL, questionText,
  isDated, periodError, periodOf, previewWithPeriod, reasonLabel, sampleText, suggestProfileName, toFailure, toMapping, uploadBlocker, valueOptions, type Draft, type Failure,
} from "@/lib/upload";


export type BodyProps = {
  fileName: string | null; preview: PreviewResponse | null; draft: Draft; busy: boolean; failure: Failure | null; error: string;
  profileName: string; profileSaved: boolean; justification: string;
  /** Dönem yazılıyor: önizleme henüz yeni dönemle yenilenmedi. */
  periodStale?: boolean;
  on: {
    file: (f: File) => void; sheet: (s: string) => void; sourceType: (t: string) => void; map: (field: string, column: string) => void;
    format: (f: NumberFormat) => void; value: (field: string, raw: string, to: string) => void;
    profileName: (s: string) => void; saveProfile: () => void; justification: (s: string) => void;
    period: (start: string, end: string) => void; full: (full: boolean) => void;
  };
};

function FailureView({ failure }: { failure: Failure }): ReactNode {
  switch (failure.kind) {
    case "already": return <p>Bu dosya {versionLabel("file", failure.uploadId)} ile zaten yüklü. Yeni versiyon açılmadı.</p>;
    case "justification":
      return <p>Bu yükleme {formatNumber(failure.changedCount)} kaydı değiştiriyor, {formatNumber(failure.removedCount)} kaydı kaldırıyor. Yüklemek için gerekçe yazın.</p>;
    case "rows":
      return (
        <div className="flex flex-col gap-2">
          <p className="font-medium">Dosya yüklenmedi. Satır hataları düzeltilmeden hiçbir satır yüklenmez.</p>
          <p>İlk {formatNumber(failure.items.length)} hata gösteriliyor (toplam {formatNumber(failure.total)}).</p>
          <div className="max-h-56 overflow-auto rounded-md border bg-background text-foreground">
            <Table>
              <TableHeader>
                <TableRow><TableHead className="bg-muted/60 text-xs font-medium text-muted-foreground text-right">Satır</TableHead><TableHead className="bg-muted/60 text-xs font-medium text-muted-foreground">Kolon</TableHead><TableHead className="bg-muted/60 text-xs font-medium text-muted-foreground">Değer</TableHead><TableHead className="bg-muted/60 text-xs font-medium text-muted-foreground">Neden</TableHead></TableRow>
              </TableHeader>
              <TableBody>
                {failure.items.map((r, i) => (
                  <TableRow key={i}>
                    <TableCell className="text-right tabular-nums">{r.duplicateRows ? r.duplicateRows.join(", ") : r.rowNo}</TableCell>
                    <TableCell>{r.column || "—"}</TableCell>
                    <TableCell className="font-mono text-xs">{r.value || "(boş)"}</TableCell>
                    <TableCell className="whitespace-normal">{reasonLabel(r.reason)}{r.duplicateRows ? ` (${r.duplicateRows.length} satır)` : ""}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      );
    default: return <ErrorLines text={failure.text} />;
  }
}

/** Numaralı bölüm başlığı (#292): Dosya → Eşleme → Profil → Gerekçe. */
function Section({ n, title, children }: { n: number; title: string; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-3">
      <h3 className="flex items-center gap-2 text-sm font-semibold">
        <span aria-hidden="true" className="flex size-5 items-center justify-center rounded-full bg-muted text-xs text-foreground">{n}</span>{title}
      </h3>
      {children}
    </section>
  );
}

/** E2b "Dönem" bölümü (ADR-0018, spec §2 adım 5). Taşınan ve kaldırılan sayılar API'den gelir; ekrandaki seçime ait değilse (yazılıyor, geçersiz) gösterilmez. */
function PeriodSection({ n, preview, draft, busy, stale, on }: { n: number; preview: PreviewResponse; draft: Draft; busy: boolean; stale: boolean; on: BodyProps["on"] }) {
  const full = !!draft.full;
  const start = draft.period?.start ?? "", end = draft.period?.end ?? "";
  const c = preview.changes;
  const err = periodError(draft);
  const shown = c && !err && !stale;
  const removedLabel = full ? "Önceki versiyondan kaldırılacak kayıt" : "Dönem içinde kaldırılacak kayıt";
  const bad = err ? { "aria-invalid": true as const, "aria-describedby": "period-error" } : {};
  return (
    <Section n={n} title="Dönem">
      <div className="flex flex-wrap items-end gap-4">
        <div className="flex flex-col gap-2">
          <Label htmlFor="period-start">Başlangıç</Label>
          <Input id="period-start" type="date" value={start} disabled={full} {...bad} onChange={(e) => on.period(e.target.value, end)} />
        </div>
        <div className="flex flex-col gap-2">
          <Label htmlFor="period-end">Bitiş</Label>
          <Input id="period-end" type="date" value={end} disabled={full} {...bad} onChange={(e) => on.period(start, e.target.value)} />
        </div>
        <label className="flex items-center gap-2 pb-2 text-sm">
          <input id="period-full" type="checkbox" checked={full} onChange={(e) => on.full(e.target.checked)} /> Tam hâl
        </label>
      </div>
      {err && <p id="period-error" className="text-sm text-destructive">{err}</p>}
      <p className="text-sm text-muted-foreground">
        {full
          ? "Dosya kaynak tipinin tam hâlidir; önceki versiyonun yerine geçer."
          : "Dosya yalnız bu dönemi kapsar. Dönem dışındaki kayıtlar önceki versiyondan taşınır."}
      </p>
      {/* Canlı bölge hep DOM'da kalır, yalnız içeriği değişir (duyuru kaybolmasın). */}
      <p role="status" aria-busy={busy} className={`text-sm ${busy ? "text-muted-foreground" : ""}`}>
        {shown && (
          <>
            {!full && <>{start && end && <>Dönem: <b>{formatDate(start)} – {formatDate(end)}</b> · </>}Taşınacak kayıt: <b>{formatNumber(c.carriedCount)}</b> · </>}
            <span className={c.removedCount > 0 ? "inline-flex items-center gap-1 font-medium text-warning-foreground" : undefined}>
              {c.removedCount > 0 && <TriangleAlert aria-hidden="true" className="size-4" />}
              {removedLabel}: <b>{formatNumber(c.removedCount)}</b>
            </span>
          </>
        )}
      </p>
    </Section>
  );
}

/** E2b gövdesi (spec §2, §3). Yalnız gösterir; durum ve istekler UploadDialog'da. */
export function UploadBody({ fileName, preview, draft, busy, failure, error, profileName, profileSaved, justification, periodStale = false, on }: BodyProps) {
  const fileInput = useRef<HTMLInputElement>(null);
  const sheetInfo = preview && (preview.sheets.find((s) => s.name === draft.sheet) ?? preview.sheets[0]);
  const fields = preview?.suggestions ?? []; // her alan için { field, required }
  const sample = (f: string) => sampleText(preview?.samples.find((s) => s.field === f), draft.sourceType);
  const ready = canUpload(preview, draft);
  const dated = isDated(draft.sourceType);

  return (
    <DropZone onFile={on.file} fileName={fileName} className="flex flex-col gap-6">
      <Section n={1} title="Dosya">
        <Label htmlFor="upload-file" className="sr-only">Excel dosyası (.xlsx)</Label>
        <div className="flex flex-wrap items-center gap-3 rounded-lg border-2 border-dashed p-4">
          {/* Tarayıcının İngilizce "Choose File" düğmesi yerine Türkçe düğme; gerçek input ekran okuyucu ve etiket için kalır. */}
          <input ref={fileInput} id="upload-file" type="file" accept=".xlsx" tabIndex={-1} className="sr-only" onChange={(e) => e.target.files?.[0] && on.file(e.target.files[0])} />
          <Button type="button" variant="outline" onClick={() => fileInput.current?.click()}>Dosya seç</Button>
          <span className="text-sm text-muted-foreground">{fileName ?? "Excel dosyasını (.xlsx) buraya bırakın ya da seçin"}</span>
        </div>
      </Section>

      {preview && sheetInfo && (
        <>
          <Section n={2} title="Eşleme">
          <div className="flex flex-wrap items-end gap-4">
            {preview.sheets.length > 1 && (
              <div className="flex flex-col gap-2">
                <Label htmlFor="upload-sheet">Sayfa</Label>
                <NativeSelect id="upload-sheet" value={sheetInfo.name} onChange={(e) => on.sheet(e.target.value)}>
                  {preview.sheets.map((s) => <option key={s.name} value={s.name}>{s.name}</option>)}
                </NativeSelect>
              </div>
            )}
            <p className="text-sm">Sayfa: <b>{sheetInfo.name}</b> · {formatNumber(sheetInfo.rowCount)} satır · {formatNumber(sheetInfo.columnCount)} kolon</p>
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="upload-source">Kaynak tipi</Label>
            <NativeSelect id="upload-source" value={draft.sourceType ?? ""} onChange={(e) => on.sourceType(e.target.value)}>
              <option value="" disabled={!!draft.sourceType}>Seçin…</option>
              {SOURCE_TYPE_CODES.map((c) => <option key={c} value={c}>{sourceTypeText(c)}</option>)}
            </NativeSelect>
            {draft.profileId !== undefined && preview.matchedProfile && (
              <p className="text-sm font-medium text-success-foreground">✓ eşleşme otomatik dolduruldu <span className="font-normal">(profil: {preview.matchedProfile.name})</span></p>
            )}
            {preview.sourceTypeGuessed && draft.sourceType && (
              <p className="text-sm text-muted-foreground">Dosya başlıklarından tahmin edildi: <b className="text-foreground">{sourceTypeText(draft.sourceType)}</b>. Doğru değilse değiştirin.</p>
            )}
          </div>

          {draft.sourceType && (
            <>
              {preview.required && <p role="status" className="text-sm font-medium">{counterText(preview.required)}</p>}
              <Table>
                <TableHeader>
                  <TableRow><TableHead className="bg-muted/60 text-xs font-medium text-muted-foreground">Sistem alanı</TableHead><TableHead className="bg-muted/60 text-xs font-medium text-muted-foreground">Dosya kolonu</TableHead><TableHead className="bg-muted/60 text-xs font-medium text-muted-foreground">Örnek (ilk dolu değer)</TableHead></TableRow>
                </TableHeader>
                <TableBody>
                  {fields.map(({ field, required }) => {
                    const s = sample(field);
                    return (
                      <TableRow key={field}>
                        <TableCell><label htmlFor={`map-${field}`}>{fieldLabel(field)}</label>{" "}<span className="text-xs text-muted-foreground">{required ? "(zorunlu)" : "(isteğe bağlı)"}</span></TableCell>
                        <TableCell>
                          <NativeSelect id={`map-${field}`} className="w-full" value={draft.mapping[field] ?? ""} onChange={(e) => on.map(field, e.target.value)}>
                            <option value="">— eşlenmedi —</option>
                            {preview.headers.map((h) => <option key={h} value={h}>{h}</option>)}
                          </NativeSelect>
                        </TableCell>
                        <TableCell className="text-xs">{s ? <><span className="font-mono">{s.raw}</span> → {s.normalized !== null ? <b>{s.normalized}</b> : <><b>okunamadı</b>{s.problem ? ` (${s.problem})` : ""}</>}</> : "—"}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </>
          )}

          {preview.questions.map((q) => (
            <fieldset key={`${q.field}-${q.column}`} className="rounded-md border border-warning-border bg-warning text-warning-foreground p-3">
              <legend className="px-1 text-sm font-medium">{fieldLabel(q.field)} ({q.column}): {questionText(q)}</legend>
              <div className="flex flex-wrap gap-4">
                {q.options.map((o) => (
                  <label key={o} className="flex items-center gap-2 text-sm">
                    <input type="radio" name={`q-${q.field}`} checked={draft.format.numberFormat === o} onChange={() => on.format(o)} /> {FORMAT_LABEL[o]}
                  </label>
                ))}
              </div>
            </fieldset>
          ))}

          {preview.unknownValues.map((u) => (
            <fieldset key={`${u.field}-${u.column}`} className="rounded-md border border-warning-border bg-warning text-warning-foreground p-3">
              <legend className="px-1 text-sm font-medium">{fieldLabel(u.field)} ({u.column}): tanınmayan değerler</legend>
              <div className="flex flex-col gap-2">
                {u.values.map((v) => (
                  <div key={v} className="flex items-center gap-2 text-sm">
                    <label htmlFor={`val-${u.field}-${v}`} className="w-40 font-mono">{v}</label>
                    <NativeSelect id={`val-${u.field}-${v}`} value="" onChange={(e) => on.value(u.field, v, e.target.value)}>
                      <option value="">Karşılığını seçin…</option>
                      {valueOptions(u.field).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </NativeSelect>
                  </div>
                ))}
              </div>
            </fieldset>
          ))}
          </Section>

          {dated && <PeriodSection n={3} preview={preview} draft={draft} busy={busy} stale={periodStale} on={on} />}

          {draft.sourceType && (
            <Section n={dated ? 4 : 3} title="Profil">
            <div className="flex flex-wrap items-end gap-2">
              <div className="flex flex-col gap-2">
                <Label htmlFor="profile-name">Profil adı</Label>
                <Input id="profile-name" value={profileName} onChange={(e) => on.profileName(e.target.value)} />
              </div>
              <Button type="button" variant="outline" disabled={busy || !profileName.trim() || !ready} onClick={on.saveProfile}>Profili kaydet</Button>
              {profileSaved && <p role="status" className="text-sm">Profil kaydedildi.</p>}
            </div>
            </Section>
          )}
        </>
      )}

      {needsJustification(preview, failure) && (
        <Section n={draft.sourceType && preview ? (dated ? 5 : 4) : 3} title="Gerekçe">
          {preview?.changes?.justificationRequired && !periodStale && !periodError(draft) && (
            <p className="text-sm">Bu yükleme {formatNumber(preview.changes.changedCount)} kaydı değiştiriyor, {formatNumber(preview.changes.removedCount)} kaydı kaldırıyor. Yüklemek için gerekçe yazın.</p>
          )}
          <Label htmlFor="justification" className="sr-only">Gerekçe</Label>
          <Textarea id="justification" required value={justification} onChange={(e) => on.justification(e.target.value)} />
        </Section>
      )}
      {(failure || error) && (
        <Alert variant="destructive">
          <AlertDescription>
            {failure && <FailureView failure={failure} />}
            {error && <ErrorLines text={error} />}
          </AlertDescription>
        </Alert>
      )}
    </DropZone>
  );
}

/** E2b penceresi (E2'nin penceresi, menü öğesi değil). Düğme yalnız `write` izni olana gösterilir (App/SourcesScreen karar verir). */
export function UploadDialog({ onUploaded, droppedFile, onDroppedFile }: { onUploaded: (r: UploadResponse) => void; droppedFile?: File | null; onDroppedFile?: () => void }) {
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<PreviewResponse | null>(null);
  const [draft, setDraft] = useState<Draft>(emptyDraft);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<Failure | null>(null);
  const [error, setError] = useState("");
  const [profileName, setProfileName] = useState("");
  const [profileSaved, setProfileSaved] = useState(false);
  const [justification, setJustification] = useState("");
  const [periodStale, setPeriodStale] = useState(false);
  const periodTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const seq = useRef(0);
  const trigger = useRef<HTMLButtonElement>(null);
  const nameTouched = useRef(false);

  const reset = () => { clearTimeout(periodTimer.current); setPeriodStale(false); seq.current++; setFile(null); setPreview(null); setDraft(emptyDraft); setFailure(null); setError(""); setProfileName(""); nameTouched.current = false; setProfileSaved(false); setJustification(""); setBusy(false); };

  /** Taslağı önizlemeye gönder. `auto`: eşleme gönderilmez, API profilden ya da öneriden doldurur. */
  async function load(f: File, d: Draft, auto = false) {
    clearTimeout(periodTimer.current);
    const n = ++seq.current;
    setDraft(d); setBusy(true); setError(""); setFailure(null); setProfileSaved(false);
    try {
      const mapping = auto ? (d.sourceType ? { sourceType: d.sourceType } : undefined) : toMapping(d);
      const { draft: dd, preview: p } = await previewWithPeriod(f, d, mapping);
      if (n !== seq.current) return;
      setDraft(draftFrom(dd, p, auto)); setPreview(p); setPeriodStale(false);
      // ADR-0013: eşleşen profil varsa ad boş başlamaz; "<ad> 2", alınmışsa sıradaki boş sayı.
      if (p.matchedProfile && !nameTouched.current) {
        const base = p.matchedProfile.name;
        getProfiles().then((all) => { if (n === seq.current && !nameTouched.current) setProfileName(suggestProfileName(base, all.map((x) => x.name))); }, () => setProfileName(suggestProfileName(base, [])));
      }
    } catch (e) {
      if (n === seq.current) setError(errorText(e));
    } finally {
      if (n === seq.current) setBusy(false);
    }
  }
  // E2 boş durumuna bırakılan dosya pencereyi açar ve doğrudan okunur (#292).
  useEffect(() => {
    if (!droppedFile) return;
    setOpen(true); setFile(droppedFile); void load(droppedFile, emptyDraft, true); onDroppedFile?.();
  }, [droppedFile]); // eslint-disable-line react-hooks/exhaustive-deps
  // Elle yapılan her değişiklik profili düşürür: yükleme artık profilin kendisi değil.
  const edit = (d: Partial<Draft>) => { if (file) void load(file, { ...draft, ...d, profileId: undefined }); };

  // Dönem değişimi önizlemeyi yeniler; yazılmakta olan (eksik ya da ters) dönem yalnız taslağa girer, Yükle nedeni söyler.
  const changePeriod = (start: string, end: string) => {
    if (!file) return;
    const next = { ...draft, period: { start, end }, full: false };
    // Tarih alanı rakam rakam yazılırken her tuşta istek gitmesin: kısa gecikmeyle yenilenir.
    clearTimeout(periodTimer.current);
    seq.current++; setBusy(false); setDraft(next); setPeriodStale(true);
    if (start && end && start <= end) periodTimer.current = setTimeout(() => void load(file, next), 400);
  };

  async function upload() {
    if (!file) return;
    setBusy(true); setUploading(true); setError(""); setFailure(null);
    try {
      const r = await createUpload(file, { sheet: draft.sheet, mapping: toMapping(draft), period: periodOf(draft), justification });
      setUploading(false); setOpen(false); reset(); onUploaded(r);
    } catch (e) {
      const fl = toFailure(e, errorText);
      if (fl.kind === "refresh") { setError(fl.text); void load(file, draft); } else setFailure(fl);
      setBusy(false); setUploading(false);
    }
  }

  async function saveCurrentProfile() {
    if (!preview || !draft.sourceType) return;
    setBusy(true); setError("");
    try {
      await saveProfile({ name: profileName.trim(), sourceType: draft.sourceType, headers: preview.headers, mapping: draft.mapping, format: draft.format, valueMap: draft.valueMap });
      setProfileSaved(true);
    } catch (e) { setError(errorText(e)); } finally { setBusy(false); }
  }

  const need = needsJustification(preview, failure) && !justification.trim();
  const blocker = uploadBlocker(preview, draft, need, !!file);
  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild><Button ref={trigger}>Dosya yükle</Button></DialogTrigger>
      <DialogContent onCloseAutoFocus={(e) => { e.preventDefault(); trigger.current?.focus(); }} className="max-h-[90svh] gap-6 overflow-y-auto sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>Dosya yükle ve kolonları eşle</DialogTitle>
          <DialogDescription>{isDated(draft.sourceType) ? "Dosya yalnız seçilen dönemi kapsar; dönem dışı kayıtlar önceki versiyondan taşınır." : "Dosya kaynak tipinin tam hâlidir; yeni yükleme önceki versiyonun yerine geçer."}</DialogDescription>
        </DialogHeader>
        <UploadBody
          fileName={file?.name ?? null} preview={preview} draft={draft} busy={busy} failure={failure} error={error}
          profileName={profileName} profileSaved={profileSaved} justification={justification} periodStale={periodStale}
          on={{
            file: (f) => { setFile(f); void load(f, emptyDraft, true); },
            sheet: (s) => file && void load(file, { ...emptyDraft, sheet: s }, true),
            sourceType: (t) => file && void load(file, { ...emptyDraft, sheet: draft.sheet, sourceType: (t || undefined) as Draft["sourceType"] }, true),
            map: (field, column) => {
              const mapping = { ...draft.mapping };
              if (column) mapping[field] = column; else delete mapping[field];
              edit({ mapping });
            },
            format: (numberFormat) => edit({ format: { ...draft.format, numberFormat } }),
            value: (field, raw, to) => to && edit({ valueMap: { ...draft.valueMap, [field]: { ...(draft.valueMap as Record<string, Record<string, string>>)[field], [raw]: to } } }),
            profileName: (s) => { nameTouched.current = true; setProfileName(s); }, saveProfile: () => void saveCurrentProfile(), justification: setJustification,
            period: changePeriod,
            full: (full) => file && void load(file, { ...draft, full, period: undefined }),
          }}
        />
        {/* Yapışkan alt çubuk: uzun eşlemede de Yükle ve nedeni ekranda kalır. */}
        <DialogFooter className="sticky -bottom-6 -mx-6 -mb-6 items-center border-t bg-card px-6 py-3 sm:justify-between">
          <p id="upload-blocker" aria-live="polite" className="text-sm text-muted-foreground">{blocker}</p>
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>Vazgeç</Button>
            <Button type="button" aria-describedby={blocker ? "upload-blocker" : undefined} disabled={busy || periodStale || !canUpload(preview, draft) || need} onClick={() => void upload()}>{uploading ? "Yükleniyor…" : "Yükle"}</Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
